import SlidingSidePanel, { SlidingPanelPage } from '../SlidingSidePanel.jsx';
import { useEffect, useState } from 'react';
import {
	fontSizeOptions,
	StylesAdvanced,
	StylesButtons,
	StylesColor,
	StylesFont,
	StylesInputs,
} from './StylesTheme.jsx';
import StylesHeader from './StylesHeader.jsx';
import StylesThemePicker from './StylesThemePicker.jsx';
import { ObjectTypes } from '~/lib/ObjectTypes.js';
import { NavList, NavListItem, NavListSwatch } from '~/components/NavList';
import { useIsPickingElement } from '~/lib/CssInspectorState';
import { XMarkIcon } from '@heroicons/react/24/outline';

const DEFAULT_COLOR = '#2C87F3';

const shapeLabels = {
	square: 'Square',
	'rounded-rectangle': 'Rounded',
	pill: 'Pill',
};

function headerSummary(styles) {
	const parts = [];
	if (styles?.headerImage) {
		parts.push('Image bg');
	} else if (styles?.headerColor) {
		parts.push('Color bg');
	}
	if (styles?.logo && !styles?.logoDisabled) {
		parts.push('custom logo');
	}
	return parts.join(', ') || 'Default';
}

function typographySummary(styles) {
	const font = styles?.font === 'serif' ? 'Serif' : 'Sans-serif';
	const size = fontSizeOptions[styles?.fontSize || 'normal'] || '';
	return `${font}, ${size.toLowerCase()}`;
}

function buttonsSummary(styles) {
	const button = styles?.button || 'rounded-rectangle';
	const isInverse = button.startsWith('inverse-');
	const shape = shapeLabels[button.replace(/^inverse-/, '')] || '';
	return isInverse ? `${shape}, outline` : shape;
}

function customCssSummary(styles) {
	return styles?.customCss ? 'Added' : 'None added';
}

/**
 * Offers to keep the styling that has just been changed.
 *
 * A form overrides the theme it uses, so changing something here only ever
 * affects this form. This is where that becomes a choice: save the look as a
 * theme of its own, or push it back into the theme so every form using it
 * follows — the latter only when the theme is one this business can edit.
 */
const ThemeChangesCallout = ({
	theme,
	onSaveAsNew,
	onUpdateTheme,
	onDismiss,
	isSaving,
}) => {
	return (
		<div className="relative bg-primary-100 border border-primary-700/30 rounded-[22px] p-4">
			<button
				type="button"
				className="absolute top-3 right-3 text-primary-700/60 hover:text-primary-700"
				onClick={onDismiss}
				aria-label="Dismiss"
			>
				<XMarkIcon className="w-[18px] h-[18px]" />
			</button>

			<div className="text-sm leading-5 text-primary-700/85 pr-6 pb-3">
				{theme
					? `You've customized the ${theme.name} theme. Save your changes as a new theme to use on other forms.`
					: 'Save these styles as a theme to use the same look on your other forms.'}
			</div>

			<div className="flex flex-wrap items-center gap-2">
				<button
					type="button"
					className="btn-pill"
					onClick={onSaveAsNew}
					disabled={isSaving}
				>
					Save as New Theme
				</button>
				{!!theme?.isEditable && (
					<button
						type="button"
						className="btn-inverse-pill"
						onClick={onUpdateTheme}
						disabled={isSaving}
					>
						Update {theme.name}
					</button>
				)}
			</div>
		</div>
	);
};

const StylesPanel = ({
	isOpen,
	onClose,
	styles,
	setStyles,
	objectId,
	objectType,
	title = 'Styles',
	type,
	customCssWarnings,
	customCssEnabled,
	// Themes. Only the form styles panel passes these
	showThemes = false,
	theme,
	themes,
	themesLoading,
	themeActions,
	isSavingTheme,
	// Whether to offer saving the current styling as, or into, a theme
	showThemeChanges = false,
	onSaveAsNewTheme,
	onUpdateTheme,
	onDismissThemeChanges,
	onPublishTheme,
}) => {
	const [page, setPage] = useState(null);
	// Picking an element means pointing at the form this panel is sitting on
	//  top of, so it gets out of the way until a choice is made
	const isPickingElement = useIsPickingElement();

	useEffect(() => {
		if (!isOpen) {
			setPage(null);
		}
	}, [isOpen]);

	const showAdvanced = type !== ObjectTypes.INVOICE && !!customCssEnabled;

	const pages = [
		{
			key: 'header',
			title: 'Header',
			content: (
				<StylesHeader
					styles={styles}
					setStyles={setStyles}
					objectId={objectId}
					objectType={objectType}
				/>
			),
		},
		{
			key: 'color',
			title: 'Accent Color',
			content: <StylesColor styles={styles} setStyles={setStyles} />,
		},
		{
			key: 'typography',
			title: 'Typography',
			content: <StylesFont styles={styles} setStyles={setStyles} />,
		},
		{
			key: 'inputs',
			title: 'Inputs',
			content: <StylesInputs styles={styles} setStyles={setStyles} />,
		},
		{
			key: 'buttons',
			title: 'Buttons',
			content: <StylesButtons styles={styles} setStyles={setStyles} />,
		},
		showAdvanced && {
			key: 'custom-css',
			title: 'Custom CSS',
			content: (
				<StylesAdvanced
					styles={styles}
					setStyles={setStyles}
					customCssWarnings={customCssWarnings}
					customCssEnabled={customCssEnabled}
				/>
			),
		},
		showThemes && {
			key: 'theme',
			title: 'Theme',
			content: (
				<StylesThemePicker
					themes={themes}
					isLoading={themesLoading}
					appliedTheme={theme}
					isSaving={isSavingTheme}
					onApply={themeId => {
						themeActions?.onApply(themeId);
						setPage(null);
					}}
					onDetach={() => {
						themeActions?.onDetach();
						setPage(null);
					}}
					onDelete={themeActions?.onDelete}
					onUnpublish={themeActions?.onUnpublish}
					onPublish={onPublishTheme}
					onEdit={themeActions?.onEdit}
					onSaveAsNewTheme={onSaveAsNewTheme}
				/>
			),
		},
	].filter(Boolean);

	return (
		<SlidingSidePanel
			isOpen={isOpen}
			onClose={onClose}
			title={title}
			isHidden={isPickingElement}
		>
			{showThemes && (
				<div className="mb-6">
					<NavList>
						<NavListItem
							label="Theme"
							detail={theme?.name || 'None'}
							onClick={() => setPage('theme')}
						/>
					</NavList>
				</div>
			)}

			<NavList label="Customize">
				<NavListItem
					label="Header"
					detail={headerSummary(styles)}
					onClick={() => setPage('header')}
				/>
				<NavListItem
					label="Accent Color"
					detail={<NavListSwatch color={styles?.color || DEFAULT_COLOR} />}
					onClick={() => setPage('color')}
				/>
				<NavListItem
					label="Typography"
					detail={typographySummary(styles)}
					onClick={() => setPage('typography')}
				/>
				<NavListItem
					label="Inputs"
					detail={shapeLabels[styles?.input || 'rounded-rectangle']}
					onClick={() => setPage('inputs')}
				/>
				<NavListItem
					label="Buttons"
					detail={buttonsSummary(styles)}
					onClick={() => setPage('buttons')}
				/>
			</NavList>

			{showAdvanced && (
				<NavList label="Advanced" className="mt-6">
					<NavListItem
						label="Custom CSS"
						detail={customCssSummary(styles)}
						onClick={() => setPage('custom-css')}
					/>
				</NavList>
			)}

			{showThemes && showThemeChanges && (
				<div className="mt-6">
					<ThemeChangesCallout
						theme={theme}
						isSaving={isSavingTheme}
						onSaveAsNew={onSaveAsNewTheme}
						onUpdateTheme={onUpdateTheme}
						onDismiss={onDismissThemeChanges}
					/>
				</div>
			)}

			{pages.map(p => (
				<SlidingPanelPage
					key={p.key}
					isOpen={page === p.key}
					onClose={() => setPage(null)}
					title={p.title}
				>
					{p.content}
				</SlidingPanelPage>
			))}
		</SlidingSidePanel>
	);
};

export default StylesPanel;
